import { css, cx } from "@emotion/css";
import { Slide, Snackbar as MaterialSnackbar } from "@mui/material";
import type { SlideProps } from "@mui/material/Slide/Slide";
import { themeTokens, space } from "@octopusdeploy/design-system-tokens";
import type React from "react";
export interface SnackbarProps {
    /**
     * Controls whether the Snackbar is being shown.
     */
    open: boolean;
    /**
     * Controls the width of the snackbar. Use `"large"` for longer content.
     */
    size?: Size;
    /**
     * Specifies how long the Snackbar will remain open, in ms. After the time has elapsed, the Snackbar will invoke the `onClose` callback.
     */
    autoHideDuration?: number;
    /**
     * The content or message that will be displayed within the Snackbar.
     */
    content: string | React.ReactNode;
    /**
     * The callback is invoked when the `autoHideDuration` has elapsed, or when the user clicks anywhere outside the Snackbar. This callback should be used to change the `open` state, controlled in a parent component.
     */
    onClose?(): void;
    /**
     * Controls the text alignment of the content within the Snackbar.
     */
    textAlign?: TextAlign;
    /**
     * Called when the transition to close the snackbar exits.
     */
    onExited?: () => void;
}
export type Size = "large" | "small";
export type TextAlign = "center" | "left";
export function Snackbar({ open, content, size, onClose, autoHideDuration, textAlign, onExited }: SnackbarProps) {
    return (<MaterialSnackbar open={open} message={content} classes={{ root: cx({ [wideStyles]: size === "large" }) }} autoHideDuration={autoHideDuration} onClose={onClose} TransitionComponent={SlideUp} TransitionProps={{ onExited }} ContentProps={{ classes: { root: cx(contentStyles, { [alignedCenterStyles]: textAlign === "center" }) } }} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}/>);
}
interface CustomSnackbarProps extends Omit<SnackbarProps, "content" | "textAlign"> {
    /**
     * Content component for the snackbar. Must have a single root element.
     */
    children: React.ReactElement;
}
/* @deprecated
Deprecated: 12/01/2026
Replacement: Snackbar
Reason: This component and will be removed in a future release, where possible use the Snackbar component instead.
*/
export function DeprecatedCustomSnackbar({ open, children, size, onClose, autoHideDuration }: CustomSnackbarProps) {
    return (<MaterialSnackbar open={open} classes={{ root: cx({ [wideStyles]: size === "large" }) }} autoHideDuration={autoHideDuration} onClose={onClose} TransitionComponent={SlideUp} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
            <div>{children}</div>
        </MaterialSnackbar>);
}
const materialSnackbarClasses = {
    root: "MuiSnackbar-root",
    contentRoot: "MuiSnackbarContent-root",
    contentMessage: "MuiSnackbarContent-message",
};
const wideStyles = css({
    [`&.${materialSnackbarClasses.root}`]: {
        width: "60%",
        margin: space[24],
        bottom: 0,
        display: "block",
    },
});
const alignedCenterStyles = css({
    [`.${materialSnackbarClasses.contentMessage}`]: {
        textAlign: "center",
        width: "100%",
    },
});
const contentStyles = css({
    [`&.${materialSnackbarClasses.contentRoot}`]: {
        backgroundColor: themeTokens.color.background.inverse.primary,
        color: themeTokens.color.text.inverse.primary,
    },
});
function SlideUp(props: Omit<SlideProps, "direction">) {
    return <Slide {...props} direction="up"/>;
}
